import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import DialogActionModal from "UIComponents/DialogActionModal";
import CriteriaRubricAssessment from "AppComponents/CriteriaRubricAssessment";
import StandardRubric from "AppComponents/StandardRubric";
import AssessmentToolV2 from "../AssessmentToolV2";
import RubricTable from "Rubric/components/RubricTable";
import SinglePointRubricTable from "SinglePointRubric/components/SinglePointRubricTable";
import ChecklistTable from "Checklist/components/ChecklistTable";
import MYPObjectiveRubric from "../AssessmentTool/MYPObjectiveRubric";
import AutomaticIBScaleTool from "../AssessmentIBScaleTool/AutomaticIBScaleTool";
import AutomaticIBScaleToolV2 from "../AssessmentIBScaleTool/AutomaticIBScaleToolV2";
import AssessmentGradeScaleTool from "AppComponents/AssessmentGradeScaleTool";
import { MarksBox } from "AppComponents/CriteriaRubricAssessment/CriteriaRubricAssessment";
import MaxScore from "../MaxScore";
import TaskWeightV2 from "../TaskWeightV2";
import { LabelComponent } from "UnitPlans/components/LabelComponent";

// Modules
import {
  updateAssessment,
  updateAssessmentInRedux,
  removeAssessmentTool,
  updateAssessmentToolSetting,
  writeAssessmentWeightageInCache,
  registerAssessmentToolUpdateActivity,
} from "Assessments/modules/AssessmentModule";
import {
  updateScoreToolData,
  writeScoreToolDataInCache,
  writeAssessmentScoreToolDataInCache,
} from "modules/AssessmentTool/AssessmentToolModule";
import { setMaxScoreValue } from "Teacher/modules/TeacherModule";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import {
  getSettingValue,
  getIsConfigurationEnabledForCPAY,
} from "modules/PermissionModule";
import { createAssessmentTool } from "AssessmentToolSetup/modules/AssessmentToolSetupModule";

// Services and Constants
import { getMYPObjectiveRubricOptions } from "modules/Services";
import { getPrintFile } from "modules/PrintThunks";
import {
  CUSTOM_WEIGHTS,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  ASSESSMENT_GRADE_BOUNDARY,
  DP_ASSESSMENT_RUBRIC,
  SCORE,
  COMMENT,
  RUBRIC,
  SINGLE_POINT_RUBRIC,
  CHECKLIST,
  MYP_OBJECTIVE_RUBRIC,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
  TODDLE_LIBRARY,
  ASSESSMENT_TYPE_AI_TUTOR,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  GRADE_SCALE_TOOL,
  WEIGHTS_PROPORTIONAL,
} from "Constants/stringConstants";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";
import { USE_NEW_GRADING_SETUP_IN_COURSE_PERM } from "Constants/permissionConstants";
import { getIsIBGradeScaleV2ForNewGradingSetupEnabled } from "Utils/ACLStoreUtils";

// Graphql helpers
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getAssessmentLearningCourseDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";

// Design System
import {
  IconButton,
  ToggleSwitch,
  Tooltip,
  Dropdown,
  DropdownMenu,
  Alert,
} from "@toddle-edu/ds-web";
import {
  DeleteOutlined,
  UserProfileOutlined,
  StudentOutlined,
  CaretClosedFilled,
  CaretOpenedFilled,
  DotsVerticalOutlined,
  InformationFilled,
  PrintOutlined,
} from "@toddle-edu/ds-icons";
import { colors, fontStyles } from "@toddle-edu/ds-theme";

// External libs.
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classNames from "classnames";

// Utils
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import ACLStore from "lib/aclStore";
import {
  getAssessmentToolText,
  getAllowTeacherAndStudentEval,
  getIsLessonPlanningEnabled,
} from "ClassRoom/modules/utils";
import {
  getGradeCalculationMethodSetting,
  getGradeCalculationMethodCategory,
} from "routes/routes/Administrator/routes/DPAcademicSetup/routes/AssessmentSetup/modules/utils";
import {
  getDropdownOptions,
  COLLAPSIBLE_TOOLS,
  TOOLS_WITH_ONLY_TEACHER_EVALUATION,
  getEvaluationText,
  DIALOGUES_INFO,
  TOOLS_WITH_TASK_WEIGHT,
} from "./Utils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import { getCategoryObj } from "../AssessmentCategory/utils";
import { getLtiToolData } from "Assessments/modules/Utils";
import { checkAssignmentContributionMethodValue } from "GradingSetupV2/modules/Utils";
import { getLtiToolDisplayName } from "Utils/AttachmentHelpers";
import { getIsScoringToolPresent } from "Utils";

// scss
import classes from "./CollapsibleAssessmentTools.scss";

const styles = {
  overlayInnerStyle: {
    maxWidth: "300px",
    padding: "8px",
    textAlign: "center",
  },
  tooltipStyle: {
    maxWidth: "248px",
  },
  weightInfoIconStyle: {
    marginLeft: "4px",
    marginRight: "20px",
  },
  mypObjectiveRubricStyles: {
    subjectLabelStyle: {
      margin: "16px 0 0 0 ",
    },
    rubricTableContainerStyle: {
      margin: "0px",
      borderRadius: "8px",
    },
    cellStyle: {
      backgroundColor: colors.surfaceWhite,
    },
    noteContainerStyle: {
      marginTop: "4px",
      marginBottom: "0px",
    },
    objectiveNameStyle: {
      margin: "20px 0",
    },
  },
  autoIBGradeToolStyles: {
    viewTableContainerStyle: {
      margin: "0px",
    },
  },
  dpRubricStyles: {
    headerStyle: { padding: "0px" },
  },
  scoreContainerStyles: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  scoreLabelContainerStyle: {
    maxWidth: "60%",
    margin: "0px",
  },
  scoreEditContainerStyle: {
    justifyContent: "flex-end",
    maxWidth: "360px",
  },
  scoreTextStyle: {
    fontSize: "1.4rem",
    ...fontStyles.demiBold,
  },
};

const renderAssessmentGradeScaleUI = params => {
  const {
    mode,
    onClickRemoveTool,
    assessmentToolId,
    assessmentToolType,
    assessmentTool,
    testId,
    setToolsRef,
    assessmentId,
    assessmentTools,
    courseId,
    sourceFrom,
  } = params;

  const onDeleteTool = e => {
    e?.stopPropagation();
    onClickRemoveTool({
      id: assessmentToolId,
      type: assessmentToolType,
    });
  };

  const setCustomRef = ref => {
    setToolsRef?.(assessmentToolType, ref);
  };

  return (
    <div className={classes.gradeScaleContainer}>
      <div className={classes.scoreInnerContainer}>
        <AssessmentGradeScaleTool
          mode={mode}
          assessmentTool={assessmentTool}
          testId={testId}
          customRef={setCustomRef}
          assessmentId={assessmentId}
          courseId={courseId}
          assessmentTools={assessmentTools}
          sourceFrom={sourceFrom}
        />
      </div>

      {mode === "edit" ? (
        <div className={classes.deleteGradeScaleIcon}>
          <IconButton
            dsVersion="2.0"
            icon={<DeleteOutlined variant="subtle" />}
            size="small"
            variant="neutral"
            type="plain"
            onClick={onDeleteTool}
            className={classes.deleteIcon}
          />
        </div>
      ) : null}
    </div>
  );
};

const renderRevampedMaxScoreWeightUI = params => {
  const {
    t,
    mode,
    onClickRemoveTool,
    assessmentToolId,
    assessmentToolType,
    parentId,
    parentType,
    assessmentId,
    scoreToolId,
    assessmentTools = [],
    assignmentId,
    testId,
    resourceFields,
    curriculumProgram,
  } = params;

  const onDeleteTool = e => {
    e?.stopPropagation();
    onClickRemoveTool({
      id: assessmentToolId,
      type: assessmentToolType,
    });
  };

  const scoreTool = _.find(assessmentTools, ({ node }) => {
    return _.get(node, "assessmentToolType") === SCORE;
  });

  const scoreToolData = _.get(
    scoreTool,
    "node.assessmentToolItem.scoreToolData",
    {}
  );

  const maxScore = _.get(scoreToolData, "maxScore", null);

  const { ltiServiceName, isLtiToolAdded, ltiScoreMaximum } = getLtiToolData({
    resourceFields,
  });

  const ltiAttachment = {
    metadata: {
      service: ltiServiceName,
    },
  };
  const translatedServiceName =
    isLtiToolAdded && ltiServiceName
      ? getLtiToolDisplayName(ltiAttachment)
      : null;

  return (
    <>
      <div className={classes.scoreContainer}>
        <div className={classes.scoreInnerContainer}>
          <MaxScore
            t={t}
            mode={mode}
            labelComponent={
              <LabelComponent
                t={t}
                label={t("unitPlanTemplate:le_DPLE_maximumScore_label")}
                labelStyle={styles.scoreTextStyle}
              />
            }
            containerStyle={styles.scoreContainerStyles}
            labelContainerStyle={styles.scoreLabelContainerStyle}
            editContainerStyle={styles.scoreEditContainerStyle}
            isCollapsibleAssessmentTool={true}
            scoreToolId={scoreToolId}
            parentId={parentId}
            parentType={parentType}
            assessmentId={assessmentId}
            assessmentTools={assessmentTools}
            maxScore={maxScore}
            testId={`${testId}-assessmentCreation-maxScore`}
            assignmentId={assignmentId}
            isLtiToolAdded={isLtiToolAdded}
            ltiScoreMaximum={ltiScoreMaximum}
            curriculumProgram={curriculumProgram}
            onDeleteScoreTool={onClickRemoveTool}
            assessmentToolId={assessmentToolId}
          />
        </div>
        {mode === "edit" ? (
          <div className={classes.deleteScoreIcon}>
            <IconButton
              dsVersion="2.0"
              icon={<DeleteOutlined variant="subtle" />}
              size="small"
              variant="neutral"
              type="plain"
              onClick={onDeleteTool}
              className={classes.deleteIcon}
            />
          </div>
        ) : null}
      </div>
      {translatedServiceName ? (
        <div className={classes.alertContainer}>
          <Alert
            dsVersion="2.0"
            type="info"
            message={t("classRoom:max_score_lti_tool_alert_message", {
              serviceName: translatedServiceName,
            })}
          />
        </div>
      ) : null}
    </>
  );
};

const ExpandCollapseToggler = props => {
  const { isExpanded, assessmentToolType } = props;

  if (!_.includes(COLLAPSIBLE_TOOLS, assessmentToolType)) return null;

  const icon = isExpanded ? (
    <CaretOpenedFilled variant="subtle" />
  ) : (
    <CaretClosedFilled variant="subtle" />
  );

  return (
    <IconButton
      dsVersion="2.0"
      icon={icon}
      size="small"
      variant="neutral"
      type="plain"
    />
  );
};

const CollapsibleAssessmentTools = props => {
  const {
    t,
    mode,
    assessmentId,
    assessmentTools = [],
    showAssessmentToolModal,
    updateAssessmentToolData,
    updateAssessmentToolSetting,
    updateInputField,
    setMaxScoreValue,
    curriculumProgramType,
    weightage,
    updateAssessmentInRedux,
    isCustomWeights,
    isWeightProportionalToMaxScore,
    isScoreEnabled,
    maxScore,
    scoreToolId,
    assessmentType,
    getPrintFile,
    parentId,
    parentType,
    showToolPrintOption,
    portalType,
    assignmentId,
    registerAssessmentToolUpdateActivity,
    academicYearId,
    testId,
    curriculumProgram,
    setToolsRef,
    isUsingNewGradingSetupInCourses,
    learningCourseId,
    updateAssessment,
    writeAssessmentWeightageInCache,
  } = props;

  // states
  const [currentDialogueAction, setCurrentDialogueAction] = useState({
    type: "",
  });
  const [expandedState, setExpandedState] = useState({});
  const [taskWeight, setTaskWeight] = useState("");

  // refs
  const textInputRef = React.useRef(null);
  const isAlreadyFocused = React.useRef(null);

  // variables
  const isPrintEnabled = !ACLStore.can(
    "FeatureFlag:PreventPrintAndScreenshots"
  );

  const onClickRemoveTool = useCallback(
    ({ id, type }) => {
      // When IB grade scale is removed and weight is proportional to max score,
      // reset task weight to 0 in both cache and backend before the tool is removed
      // (TaskWeightV2 will unmount and won't be able to do this itself).
      const isDP = curriculumProgramType === "IB_DP";
      const isGradeScaleBeingRemoved = type === ASSESSMENT_GRADE_BOUNDARY;
      if (
        isGradeScaleBeingRemoved &&
        isDP &&
        isUsingNewGradingSetupInCourses &&
        isWeightProportionalToMaxScore &&
        !_.isEmpty(_.toString(weightage))
      ) {
        writeAssessmentWeightageInCache({
          assessmentId,
          weightage: 0,
        });
        updateAssessment({ hasFields: false, weightage: 0 });
      }

      updateInputField(
        {
          measureAssessing: type,
        },
        { id, type }
      );
    },
    [
      updateInputField,
      curriculumProgramType,
      isUsingNewGradingSetupInCourses,
      isWeightProportionalToMaxScore,
      weightage,
      assessmentId,
      updateAssessment,
      writeAssessmentWeightageInCache,
    ]
  );

  // side effects
  useEffect(() => {
    if (isScoreEnabled) {
      setMaxScoreValue(maxScore);
      if (_.isEmpty(_.toString(maxScore)) && !isAlreadyFocused.current) {
        textInputRef.current?.focus();
        isAlreadyFocused.current = true;
      }
    }
  }, [isScoreEnabled]);

  useEffect(() => {
    const value = _.toString(weightage);
    if (!_.isEqual(value, taskWeight)) {
      setTaskWeight(value);
    }
  }, []);

  // if a new tool is added then collapse existing ones
  useEffect(() => {
    if (_.size(assessmentTools) > 1) {
      const newExpandedState = {};
      _.forEach(assessmentTools, ({ node }, index) => {
        newExpandedState[node?.assessmentToolType] =
          mode === "edit" && index === _.size(assessmentTools) - 1;
      });

      setExpandedState(newExpandedState);
    }
  }, [_.size(assessmentTools)]);

  useEffect(() => {
    return () => {
      updateAssessmentInRedux({ maxScore: null });
    };
  }, []);

  const toggleAssessmentToolModal = () => {
    updateAssessmentToolData({
      showAssessmentToolModal: !showAssessmentToolModal,
    });
  };

  const onChangeEvaluation = params => {
    const {
      toolId,
      toolType,
      isTeacherEvaluationEnable,
      isStudentEvaluationEnable,
      isTeacherEvaluation = false,
    } = params;

    let mutationParams = {
      toolId,
      toolType,
      parentId: assessmentId,
      parentType: "ASSESSMENT",
      portalType,
      academicYearId,
    };

    if (isTeacherEvaluation) {
      mutationParams = {
        ...mutationParams,
        isTeacherEvaluationEnable,
      };
    } else {
      mutationParams = {
        ...mutationParams,
        isStudentEvaluationEnable,
      };
    }

    updateAssessmentToolSetting(mutationParams);
  };

  const onClickToolSave = params => {
    setCurrentDialogueAction({
      type: "SAVE_TEMPLATE",
      ...params,
    });
  };

  const renderTool = params => {
    const {
      assessmentToolType,
      assessmentTool,
      assessmentToolText,
      assessmentToolId,
    } = params;

    const {
      updateInputField,
      unitPlanFields,
      field_list,
      assessmentFields,
      courseId,
      academicTermId,
      category: rawCategory,
      globalCategories,
      isUsingNewGradingSetupInCourses,
      portalType,
    } = props;

    const category = getCategoryObj({
      globalCategories,
      category: rawCategory,
      isUsingNewGradingSetupInCourses,
    });

    if (_.isEmpty(assessmentTool)) return null;

    const _onClickToolSave = () => {
      onClickToolSave({
        assessmentToolText,
        assessmentToolId,
        assessmentToolType,
      });
    };

    const setCustomRef = ref => {
      setToolsRef?.(assessmentToolType, ref);
    };

    switch (assessmentToolType) {
      case RUBRIC: {
        return (
          <RubricTable
            mode={mode}
            rubric={assessmentTool}
            onClickToolSave={_onClickToolSave}
            showActionButtons={true}
            minRows={1}
            firstColumnHeaderText={t("common:criteria")}
            actionButtonsUIType={"ICON_BUTTON"}
            showHoverState={mode === "edit"}
            scrollToLastCriteria={true}
            portalType={portalType}
            assignmentId={assignmentId}
            uiType={"COMPACT"}
            isEnabledAssessmentToolReordering={true}
            testId={testId}
            customRef={setCustomRef}
          />
        );
      }
      case CHECKLIST:
        return (
          <ChecklistTable
            mode={mode}
            checklist={assessmentTool}
            onClickToolSave={_onClickToolSave}
            showActionButtons={true}
            actionButtonsUIType={"ICON_BUTTON"}
            showHoverState={mode === "edit"}
            showHeader={false}
            portalType={portalType}
            assignmentId={assignmentId}
            uiType={"COMPACT"}
            isEnabledAssessmentToolReordering={true}
            customRef={setCustomRef}
          />
        );
      case SINGLE_POINT_RUBRIC:
        return (
          <SinglePointRubricTable
            mode={mode}
            rubric={assessmentTool}
            onClickToolSave={_onClickToolSave}
            showActionButtons={true}
            actionButtonsUIType={"ICON_BUTTON"}
            isSplitView={true}
            portalType={portalType}
            assignmentId={assignmentId}
            uiType={"COMPACT"}
            isEnabledAssessmentToolReordering={true}
            testId={testId}
            customRef={setCustomRef}
          />
        );
      case MYP_OBJECTIVE_RUBRIC:
      case MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC:
        return (
          <MYPObjectiveRubric
            {...props}
            assessmentTool={assessmentTool}
            options={getMYPObjectiveRubricOptions({
              unitPlanFields,
              field_list,
              assessmentFields,
              measureAssessingValue: assessmentToolType,
            })}
            updateInputField={updateInputField}
            isCreateMode={false}
            toolType={assessmentToolType}
            showObjectiveSelection={false}
            {...styles.mypObjectiveRubricStyles}
            isMYPObjectiveRubricNewUI={true}
            assignmentId={assignmentId}
            customRef={setCustomRef}
          />
        );
      case DP_ASSESSMENT_RUBRIC: {
        const criteriaData = [
          {
            criteria: _.get(assessmentTool, "criteria", []),
            hasMarksBox: false,
          },
        ];

        return (
          <CriteriaRubricAssessment
            typeOfScale={"unMarked"}
            criteriaData={criteriaData}
            assessmentHeading={_.get(assessmentTool, "label", "")}
            showCriterionSelection={false}
            isAssessmentToolEditMode={false}
            assessmentTool={assessmentTool}
            {...styles.dpRubricStyles}
            sourceFrom={"CLASSROOM"}
            customRef={setCustomRef}
            isSelectionModalMode={true}
            showAssessmentLabelAsRootNode={true}
            hideRootNodeLabel={true}
          />
        );
      }

      case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY: {
        const IBScaleTool =
          isUsingNewGradingSetupInCourses &&
          getIsIBGradeScaleV2ForNewGradingSetupEnabled()
            ? AutomaticIBScaleToolV2
            : AutomaticIBScaleTool;
        return (
          <IBScaleTool
            {...props}
            mode={"preview"}
            assessmentTool={assessmentTool}
            showModalInViewModal={false}
            selectedCourseId={courseId}
            categoryId={category?.id}
            academicTermId={academicTermId}
            assessmentFields={assessmentFields}
            category={category}
            showAccordian={false}
            showTitle={false}
            {...styles.autoIBGradeToolStyles}
            customRef={setCustomRef}
          />
        );
      }

      case SCORE_BASED_RUBRIC:
      case STANDARD_BASED_RUBRIC: {
        return (
          <StandardRubric
            mode={mode}
            assessmentTool={assessmentTool}
            onClickToolSave={_onClickToolSave}
            showActionButtons={true}
            portalType={portalType}
            assignmentId={assignmentId}
            customRef={setCustomRef}
            type={assessmentToolType}
            learningCourseId={learningCourseId}
            assessmentId={assessmentId}
            uiType={"COMPACT"}
          />
        );
      }

      default:
        return null;
    }
  };

  const saveAssessmentTool = async title => {
    const {
      createAssessmentTool,
      filters,
      curriculumProgramId,
      portalType,
    } = props;

    const { assessmentToolId, assessmentToolType } =
      currentDialogueAction || {};

    await createAssessmentTool({
      copyToolId: assessmentToolId,
      title,
      type: assessmentToolType,
      mode: "COPY",
      filters,
      curriculumProgramId,
      portalType,
    });
  };

  const onDialogConfirmClick = async e => {
    switch (currentDialogueAction?.type) {
      case "SAVE_TEMPLATE":
        saveAssessmentTool(e);
        break;
    }

    setCurrentDialogueAction({ type: "" });
  };

  const renderExpandedStateUI = ({ assessmentToolType }) => {
    switch (assessmentToolType) {
      case DP_ASSESSMENT_RUBRIC: {
        const rubric = _.find(assessmentTools, ({ node }) => {
          return _.get(node, "assessmentToolType") === assessmentToolType;
        });

        const criteria = _.get(rubric, "node.assessmentToolItem.criteria", []);
        const criterionIds = _.map(criteria, "id");

        return (
          <MarksBox
            t={t}
            isRootNode={true}
            isParentNode={true}
            criterionIds={criterionIds}
            criteria={criteria}
          />
        );
      }
      default:
        return null;
    }
  };

  const renderOptions = params => {
    const {
      assessmentToolId,
      assessmentToolType,
      isTeacherEvaluationEnabled,
      isStudentEvaluationEnabled,
      isExpanded,
    } = params;

    const canPrint = _.get(
      _.find(ASSESSMENT_TOOLS, { type: assessmentToolType }),
      "canPrint"
    );

    const onClickPrintTool = e => {
      e?.stopPropagation();
      const assessmentToolKey = _.get(
        _.find(ASSESSMENT_TOOLS, { type: assessmentToolType }),
        "key"
      );

      const printType = _.get(
        _.find(ASSESSMENT_TOOLS, { type: assessmentToolType }),
        "printType"
      );
      const printId =
        assessmentToolKey === "mypObjectiveRubric"
          ? parentId
          : assessmentToolId;

      getPrintFile({ id: printId || assessmentId, type: printType });
    };

    const onClickDropdownMenu = e => {
      e.domEvent.stopPropagation();
      switch (e.key) {
        case "edit_tool":
          updateAssessmentToolData({
            assessmentToolModalMode: "edit_tool",
          });
          updateAssessmentInRedux({
            assessmentToolId,
            assessmentToolType,
          });
          toggleAssessmentToolModal();
          break;
        case "download_tool":
          onClickPrintTool();
          break;
        case "remove_tool":
          onClickRemoveTool({
            id: assessmentToolId,
            type: assessmentToolType,
          });
          break;
      }
    };

    let rightButton = null;

    if (mode === "edit") {
      if (canPrint) {
        const options = getDropdownOptions({
          t,
          type: assessmentToolType,
          assessmentTools,
          canPrint,
          canEdit: false,
        });

        const isStandardOrScoreBasedRubric = [
          SCORE_BASED_RUBRIC,
          STANDARD_BASED_RUBRIC,
        ].includes(assessmentToolType);

        // dropdown menu is interfering with popover icons of score and standard based rubric
        const placement = isStandardOrScoreBasedRubric
          ? "topRight"
          : "bottomLeft";

        rightButton = (
          <Dropdown
            overlayClassName={classes.dropdownOverlay}
            overlay={
              <DropdownMenu
                testId={`${testId}-assessmentCreation-${_.camelCase(
                  assessmentToolType
                )}-actionMenu`}
                options={options}
                onClick={onClickDropdownMenu}
              />
            }
            getPopupContainer={el =>
              document.getElementById("measureAssessing") || el
            }
            placement={placement}
          >
            <IconButton
              dsVersion="2.0"
              onClick={e => e.stopPropagation()}
              icon={<DotsVerticalOutlined variant="subtle" />}
              variant="neutral"
              type="plain"
              size="small"
              testId={`${testId}-assessmentCreation-${_.camelCase(
                assessmentToolType
              )}-actionMenu`}
            />
          </Dropdown>
        );
      } else {
        rightButton = (
          <IconButton
            dsVersion="2.0"
            icon={<DeleteOutlined variant="subtle" />}
            size="small"
            variant="neutral"
            type={"plain"}
            onClick={e => {
              e.stopPropagation();
              onClickRemoveTool({
                id: assessmentToolId,
                type: assessmentToolType,
              });
            }}
            className={classes.deleteIcon}
          />
        );
      }
    } else {
      if (
        mode === "view" &&
        canPrint &&
        showToolPrintOption &&
        isPrintEnabled
      ) {
        rightButton = (
          <IconButton
            dsVersion="2.0"
            icon={<PrintOutlined size={"xx-small"} variant="subtle" />}
            size={"x-small"}
            variant="neutral"
            type="plain"
            onClick={onClickPrintTool}
          />
        );
      }
    }

    switch (assessmentToolType) {
      case DP_ASSESSMENT_RUBRIC:
      case MYP_OBJECTIVE_RUBRIC:
      case MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC:
      case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY: {
        const overlay = (
          <DropdownMenu
            onClick={onClickDropdownMenu}
            options={getDropdownOptions({
              t,
              type: assessmentToolType,
              assessmentTools,
              canPrint,
            })}
          />
        );

        return (
          <div className={classes.assessmentEvaluation}>
            {isExpanded ? (
              renderExpandedStateUI({ assessmentToolType })
            ) : (
              <div className={classes.evalEnabledText}>
                {getEvaluationText({
                  t,
                  isTeacherEvaluationEnabled,
                  isStudentEvaluationEnabled,
                  isExpanded,
                })}
              </div>
            )}
            {mode === "edit" ? (
              <Dropdown
                overlayClassName={classes.dropdownOverlay}
                getPopupContainer={el =>
                  document.getElementById("measureAssessing") || el
                }
                overlay={overlay}
                placement={"bottomRight"}
              >
                <IconButton
                  dsVersion="2.0"
                  onClick={e => e.stopPropagation()}
                  icon={<DotsVerticalOutlined variant="subtle" />}
                  variant="neutral"
                  type="plain"
                  size="small"
                  testId={`${testId}-assessmentCreation-${_.camelCase(
                    assessmentToolType
                  )}-actionMenu`}
                />
              </Dropdown>
            ) : (
              rightButton
            )}
          </div>
        );
      }
      default: {
        if (assessmentToolType === COMMENT && mode === "view") {
          return <ToggleSwitch checked disabled />;
        }

        return (
          <div className={classes.assessmentEvaluation}>
            {!isExpanded ? (
              <div className={classes.evalEnabledText}>
                {getEvaluationText({
                  t,
                  isTeacherEvaluationEnabled,
                  isStudentEvaluationEnabled,
                  isExpanded,
                })}
              </div>
            ) : null}
            {rightButton}
          </div>
        );
      }
    }
  };

  const renderEvaluationSwitch = params => {
    const {
      assessmentToolId,
      assessmentToolType,
      assessmentToolText,
      isTeacherEvaluationEnabled,
      isStudentEvaluationEnabled,
    } = params;

    if (mode !== "edit") return null;

    const {
      allowTeacherEval,
      allowStudentEval,
      toolHasEval,
    } = getAllowTeacherAndStudentEval({
      assessmentToolType,
    });

    const showSwitches =
      !_.includes(TOOLS_WITH_ONLY_TEACHER_EVALUATION, assessmentToolType) &&
      assessmentType !== ASSESSMENT_TYPE_AI_TUTOR;

    return showSwitches ? (
      <div className={classes.headerRight}>
        <div className={classes.switchContainer}>
          <div className={classes.flexWithGap}>
            {toolHasEval && allowTeacherEval ? (
              <>
                <Tooltip
                  tooltip={t("common:teacher_evaluation")}
                  placement={"top"}
                  showArrow={true}
                >
                  <UserProfileOutlined variant="subtle" />
                </Tooltip>
                <ToggleSwitch
                  testId={`${testId}-teacherEvaluation`}
                  size="medium"
                  checked={isTeacherEvaluationEnabled}
                  onChange={e =>
                    onChangeEvaluation({
                      toolId: assessmentToolId,
                      toolType: assessmentToolType,
                      isTeacherEvaluationEnable: e.target.checked,
                      isTeacherEvaluation: true,
                    })
                  }
                  disabled={_.includes(
                    TOOLS_WITH_ONLY_TEACHER_EVALUATION,
                    assessmentToolType
                  )}
                />
              </>
            ) : (
              <span>{"-"}</span>
            )}
          </div>

          <div className={classes.separator} />
          <div className={classes.flexWithGap}>
            {toolHasEval && allowStudentEval ? (
              <>
                <Tooltip
                  tooltip={t("common:student_evaluation")}
                  placement={"top"}
                  showArrow={true}
                >
                  <StudentOutlined variant="subtle" />
                </Tooltip>
                <ToggleSwitch
                  testId={`${testId}-studentEvaluation`}
                  size="medium"
                  checked={isStudentEvaluationEnabled}
                  onChange={e =>
                    onChangeEvaluation({
                      toolId: assessmentToolId,
                      toolType: assessmentToolType,
                      isStudentEvaluationEnable: e.target.checked,
                    })
                  }
                />
              </>
            ) : (
              <Tooltip
                placement={"top"}
                tooltip={t("common:student_eval_not_applicable_for_tool", {
                  label: t("common:lowercase", {
                    text: assessmentToolText,
                  }),
                })}
                showArrow={true}
              >
                <span>{"-"}</span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    ) : null;
  };

  const assessmentToolName = _.get(
    currentDialogueAction,
    "assessmentToolText",
    ""
  );

  const renderAssessmentTools =
    !_.isEmpty(assessmentTools) && !showAssessmentToolModal;

  const hasToolWithMaxScore = _.some(assessmentTools, ({ node }) => {
    const assessmentToolType = _.get(node, "assessmentToolType", "");

    switch (assessmentToolType) {
      case SCORE:
      case SCORE_BASED_RUBRIC:
        return true;
      case STANDARD_BASED_RUBRIC: {
        const associateScoreTool = _.get(
          node,
          "assessmentToolItem.associateScoreTool",
          null
        );

        return !_.isNil(associateScoreTool);
      }
      default:
        return false;
    }
  });

  // Find the index of the last tool that supports task weight
  const isDP = curriculumProgramType === "IB_DP";
  const lastTaskWeightToolIndex = _.findLastIndex(
    assessmentTools,
    ({ node }) => {
      const assessmentToolType = _.get(node, "assessmentToolType", "");
      const assessmentToolItem = _.get(node, "assessmentToolItem", {});

      // For IB_DP, SCORE and COMMENT tools are not rendered, so skip them
      if (isDP && _.includes([SCORE, COMMENT], assessmentToolType)) {
        return false;
      }

      // For DP with grade scale (new grading setup + proportional to max score), show task weight below it
      if (
        isDP &&
        isUsingNewGradingSetupInCourses &&
        isWeightProportionalToMaxScore &&
        _.includes([ASSESSMENT_GRADE_BOUNDARY], assessmentToolType)
      ) {
        return true;
      }

      // For standard-based rubric, skip only when grading type is not yet configured
      if (assessmentToolType === STANDARD_BASED_RUBRIC) {
        const gradingType = _.get(
          assessmentToolItem,
          "gradeScale.gradingType",
          null
        );
        if (!gradingType) {
          return false;
        }
      }

      // Task weight is always shown below the tool for custom weights.
      // For proportional weights, shown only if no tool already provides a max score
      // and no scoring tool is present (scoring tools disable task weight, so no point showing it).
      const shouldShowTaskWeight =
        isCustomWeights ||
        (isWeightProportionalToMaxScore &&
          !hasToolWithMaxScore &&
          !getIsScoringToolPresent(assessmentTools));

      return (
        _.includes(TOOLS_WITH_TASK_WEIGHT, assessmentToolType) &&
        shouldShowTaskWeight
      );
    }
  );

  // Check if any tool requiring task weight is present
  const hasToolWithTaskWeight = lastTaskWeightToolIndex !== -1;

  const isDPWithGradeScale =
    isDP &&
    isUsingNewGradingSetupInCourses &&
    isWeightProportionalToMaxScore &&
    _.some(assessmentTools, ({ node }) =>
      _.includes([ASSESSMENT_GRADE_BOUNDARY], _.get(node, "assessmentToolType"))
    );

  const setTaskWeightRef = ref => {
    setToolsRef?.("TASK_WEIGHT", ref);
  };

  const renderTaskWeight = ({ assessmentToolType } = {}) => {
    if (!hasToolWithTaskWeight) return null;

    const containerClass = classNames({
      [classes.container]: true,
      [classes.taskWeightWithPadding]: assessmentToolType === SCORE,
    });

    const isDPMaxScoreFilled =
      isDPWithGradeScale && !_.isEmpty(_.toString(maxScore));
    const taskWeightSubText = isDPMaxScoreFilled
      ? t("classRoom:dp_weight_proportional_hint")
      : t("unitPlanTemplate:le_DPLE_weightage_subtext");

    return (
      <div key="task-weight" className={containerClass}>
        <div className={classes.scoreContainer}>
          <div className={classes.scoreInnerContainer}>
            <TaskWeightV2
              t={t}
              taskWeight={taskWeight}
              label={t("unitPlanTemplate:le_DPLE_weightage_label")}
              labelComponent={
                <LabelComponent
                  t={t}
                  label={t("unitPlanTemplate:le_DPLE_weightage_label")}
                  labelStyle={styles.scoreTextStyle}
                  subText={taskWeightSubText}
                />
              }
              containerStyle={styles.scoreContainerStyles}
              labelContainerStyle={styles.scoreLabelContainerStyle}
              editContainerStyle={styles.scoreEditContainerStyle}
              isCollapsibleAssessmentTool={true}
              weightage={weightage}
              scoreToolId={scoreToolId}
              parentId={parentId}
              parentType={parentType}
              assessmentId={assessmentId}
              assessmentTools={assessmentTools}
              testId={`${testId}-assessmentCreation-taskWeight`}
              curriculumProgram={curriculumProgram}
              isDPWithGradeScale={isDPWithGradeScale}
              dpMaxScore={maxScore}
              isCustomWeights={isCustomWeights}
              customRef={setTaskWeightRef}
            />
          </div>
        </div>
      </div>
    );
  };

  const isWorksheet = assessmentType == ASSESSMENT_TYPE_WORKSHEET;

  return (
    <React.Fragment>
      {renderAssessmentTools
        ? _.map(assessmentTools, ({ node }, index) => {
            const nodeId = _.get(node, "id");
            const assessmentTool = _.get(node, "assessmentToolItem", {});
            const assessmentToolId = _.get(node, "assessmentToolItem.id", {});
            const assessmentToolType = _.get(node, "assessmentToolType", "");

            // in worksheet we remove score tool from the dropdown
            if (
              assessmentType == ASSESSMENT_TYPE_WORKSHEET &&
              _.includes([SCORE], assessmentToolType)
            ) {
              return;
            }
            if (
              curriculumProgramType === "IB_DP" &&
              _.includes([SCORE, COMMENT], assessmentToolType)
            ) {
              return;
            }

            const isTeacherEvaluationEnabled = _.get(
              node,
              "isTeacherEvaluationEnable",
              false
            );
            const isStudentEvaluationEnabled = _.get(
              node,
              "isStudentEvaluationEnable",
              false
            );

            const { assessmentToolText, subtext } = getAssessmentToolText({
              t,
              assessmentToolType,
              assessmentTool,
              isCustomWeights,
            });

            const isExpanded =
              _.get(expandedState, assessmentToolType, true) &&
              _.includes(COLLAPSIBLE_TOOLS, assessmentToolType);

            const isLessonPlanningEnabled = getIsLessonPlanningEnabled(
              assessmentType
            );

            const onExpandOrCollapse = e => {
              e.stopPropagation();
              setExpandedState(prevState => {
                const value = !_.isUndefined(prevState?.[assessmentToolType])
                  ? !prevState?.[assessmentToolType]
                  : false;

                return {
                  ...prevState,
                  [`${assessmentToolType}`]: value,
                };
              });
            };

            const onKeyDown = e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onExpandOrCollapse(e);
              }
            };

            const handleBlur = () => {
              registerAssessmentToolUpdateActivity({
                assignmentId,
                toolType: assessmentToolType,
              });
            };

            const containerClass = classNames({
              [classes.container]: true,
              [classes.containerV2]: isWorksheet,
            });

            const headerClass = classNames({
              [classes.headerContainer]: true,
              [classes.viewHeaderCompact]: mode === "view",
            });

            const bodyContainerClass = classNames({
              [classes.bodyContainer]: true,
              [classes.dpRubricBodyContainer]:
                assessmentToolType === DP_ASSESSMENT_RUBRIC,
            });

            const isScoreTool = assessmentToolType === SCORE;
            const isAssessmentGradeScale =
              assessmentToolType === GRADE_SCALE_TOOL;

            const renderToolContent = () => {
              if (isScoreTool) {
                return renderRevampedMaxScoreWeightUI({
                  ...props,
                  assessmentToolId,
                  assessmentToolType,
                  scoreToolId,
                  taskWeight,
                  onClickRemoveTool,
                  assessmentTools,
                  isCustomWeights,
                  assignmentId,
                  curriculumProgram,
                });
              }

              if (isAssessmentGradeScale) {
                return renderAssessmentGradeScaleUI({
                  ...props,
                  assessmentToolId,
                  assessmentToolType,
                  assessmentTool,
                  onClickRemoveTool,
                });
              }

              return (
                <header id={`${assessmentToolType}`} className={headerClass}>
                  <div className={classes.headerLeft}>
                    <div
                      className={classes.flexWithGap}
                      onClick={onExpandOrCollapse}
                      onKeyDown={onKeyDown}
                      tabIndex={0}
                      aria-label={`${
                        isExpanded ? "Collapse" : "Expand"
                      } ${assessmentToolText}`}
                      role="button"
                    >
                      {assessmentToolText}
                      {isCustomWeights && assessmentToolType === SCORE ? (
                        <Tooltip
                          placement={"bottom"}
                          tooltip={t("classRoom:task_weight_msg")}
                          overlayInnerStyle={styles.overlayInnerStyle}
                          showArrow={true}
                        >
                          <InformationFilled
                            variant={"subtle"}
                            size={"xx-small"}
                          />
                        </Tooltip>
                      ) : null}
                      <ExpandCollapseToggler
                        isExpanded={isExpanded}
                        assessmentToolType={assessmentToolType}
                      />
                    </div>
                    {subtext ? (
                      <div className={classes.subtext}>{subtext}</div>
                    ) : null}
                  </div>

                  <div className={classes.switchWithThreeDots}>
                    {isExpanded
                      ? renderEvaluationSwitch({
                          isExpanded,
                          assessmentToolId,
                          assessmentToolType,
                          assessmentToolText,
                          isTeacherEvaluationEnabled,
                          isStudentEvaluationEnabled,
                        })
                      : null}

                    {renderOptions({
                      assessmentToolId,
                      assessmentToolType,
                      isTeacherEvaluationEnabled,
                      isStudentEvaluationEnabled,
                      isExpanded,
                    })}
                  </div>
                </header>
              );
            };

            // Render tool and task weight if this is the last tool that supports task weight
            const shouldRenderTaskWeightAfterThisTool =
              index === lastTaskWeightToolIndex;

            return (
              <React.Fragment key={nodeId}>
                <div className={containerClass}>
                  {renderToolContent()}

                  {/* Body */}

                  {isExpanded ? (
                    <div className={bodyContainerClass} onBlur={handleBlur}>
                      <div className={classes.toolContainer}>
                        {renderTool({
                          assessmentTool,
                          assessmentToolType,
                          assessmentToolText,
                          assessmentToolId,
                          isLessonPlanningEnabled,
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Render task weight after the last tool that supports it */}
                {shouldRenderTaskWeightAfterThisTool
                  ? renderTaskWeight({ assessmentToolType })
                  : null}
              </React.Fragment>
            );
          })
        : null}

      {showAssessmentToolModal ? (
        <AssessmentToolV2 mode={"edit"} parentId={parentId} {...props} />
      ) : null}

      {!_.isEmpty(currentDialogueAction?.type) ? (
        <DialogActionModal
          modalTitle={DIALOGUES_INFO[currentDialogueAction.type].title({
            t,
            label: assessmentToolName,
          })}
          onClickButton2={onDialogConfirmClick}
          message={DIALOGUES_INFO[currentDialogueAction.type].message(
            t,
            assessmentToolName
          )}
          onClose={() => setCurrentDialogueAction({})}
          button1={DIALOGUES_INFO[currentDialogueAction.type].cancelButtonText(
            t
          )}
          button2={DIALOGUES_INFO[currentDialogueAction.type].confirmButtonText(
            t
          )}
          isPromptBox={currentDialogueAction.type === "SAVE_TEMPLATE"}
          promptDetail={{
            placeholder: t("unitPlan:enter_template_title"),
            checkValidation: true,
          }}
          testId={`${testId}-assessmentCreation`}
          dsVersion={"2.0"}
        />
      ) : null}
    </React.Fragment>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    assessmentToolData,
    curriculumProgramType,
    curriculumProgram,
    learningCourseId,
    assessmentFields,
  } = ownProps;
  const { showAssessmentToolModal } = assessmentToolData;

  const assessmentSubjectIds =
    _.find(assessmentFields, { uid: "subjects" })?.value || [];

  const { assessmentId = "" } = state.assessment.assessmentData;
  let selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course",
    ""
  );

  selectedCourseId = !_.isEmpty(selectedCourseId)
    ? selectedCourseId
    : ownProps?.selectedCourseId;

  const paramsCourseId = _.get(ownProps, "params.course_id");

  const courseId = paramsCourseId ? paramsCourseId : selectedCourseId;
  const academicYearId = getSelectedAcademicYearId(state);
  const courseGradeFilters = {
    academicYearId,
  };

  let subjectName, primaryGradeId;
  if (getIsLearningCoursesEnabled()) {
    const learningCourseDetails = getAssessmentLearningCourseDetailsFromCache({
      id: _.get(ownProps, "learningCourseId"),
    });
    subjectName = _.get(learningCourseDetails, "subjects.0.name", "");
    primaryGradeId = _.get(learningCourseDetails, "primaryGrade.id");
  } else {
    const courseDetails = getCourseDetailsFromCache({
      id: courseId,
      courseGradeFilters,
    });
    subjectName = _.get(courseDetails, "subjects.0.name", "");
    primaryGradeId = _.get(courseDetails, "primaryGrade.id");
  }

  const isToddleLibrary = state.login.activeTab == TODDLE_LIBRARY;

  const curriculumProgramId =
    (isToddleLibrary
      ? _.get(state, "classRoom.curriculumProgram.id")
      : _.get(state, "platform.currentCurriculumProgram.id", null)) ||
    _.get(curriculumProgram, "id");

  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
    curriculumProgramId: curriculumProgramId,
    academicYearId: academicYearId,
  });

  const gradeCalculationMethodSettingName = getGradeCalculationMethodSetting({
    curriculumType: curriculumProgramType,
  });

  let isCustomWeights = false,
    isWeightProportionalToMaxScore = false;

  if (isUsingNewGradingSetupInCourses) {
    isCustomWeights = checkAssignmentContributionMethodValue({
      curriculumProgramType,
      learningCourseId,
      subjectIds: assessmentSubjectIds,
      primaryGradeId,
      academicYearId,
    });

    isWeightProportionalToMaxScore = checkAssignmentContributionMethodValue({
      learningCourseId,
      curriculumProgramType,
      subjectIds: assessmentSubjectIds,
      primaryGradeId,
      methods: [WEIGHTS_PROPORTIONAL],
      academicYearId,
    });
  } else {
    const gradeCalculationMethod = getSettingValue({
      name: gradeCalculationMethodSettingName,
      currentCurriculumProgramId: curriculumProgramId,
    });

    isCustomWeights = _.isEqual(gradeCalculationMethod, CUSTOM_WEIGHTS);
    isWeightProportionalToMaxScore = _.isEqual(
      gradeCalculationMethod,
      CUSTOM_WEIGHTS
    );
  }

  const scoreDetails = getScoreDetailsMemoize({
    assessmentTools: {
      edges: _.get(ownProps, "assessmentTools", []),
    },
  });

  return {
    assessmentId,
    showAssessmentToolModal,
    subjectName,
    curriculumProgramType,
    isCustomWeights,
    isWeightProportionalToMaxScore,
    isUsingNewGradingSetupInCourses,
    platform: "ADMIN",
    categories: [
      getGradeCalculationMethodCategory({
        curriculumType: curriculumProgramType,
      }),
    ],
    curriculumProgramId,
    gradeCalculationMethodSettingName,
    ...scoreDetails,
    academicYearId,
  };
};

const mapActionCreators = {
  updateAssessment,
  updateAssessmentInRedux,
  removeAssessmentTool,
  updateAssessmentToolSetting,
  updateScoreToolData,
  writeScoreToolDataInCache,
  writeAssessmentScoreToolDataInCache,
  setMaxScoreValue,
  createAssessmentTool,
  getPrintFile,
  writeAssessmentWeightageInCache,
  registerAssessmentToolUpdateActivity,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({ resource: ["common", "classRoom"] })
)(CollapsibleAssessmentTools);
